import type { ComponentProps, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";

export function ConfidentialLeaveDialog({
  trigger,
  onLeave,
  open,
  onOpenChange,
  isLeaving = false,
  onCloseAutoFocus,
}: {
  trigger?: ReactNode;
  onLeave: () => void | Promise<void>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isLeaving?: boolean;
  onCloseAutoFocus?: ComponentProps<typeof AlertDialogContent>["onCloseAutoFocus"];
}) {
  const { t } = useTranslation();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {trigger ? (
        <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      ) : null}
      <AlertDialogContent onCloseAutoFocus={onCloseAutoFocus} className="w-[min(346px,calc(100vw-32px))] gap-0 overflow-hidden rounded-2xl border-[var(--confidential-dialog-border)] bg-background p-0">
        <AlertDialogHeader className="gap-2 p-6 text-start">
          <AlertDialogTitle className="text-base font-medium leading-[1.6] text-foreground">
            {t("playground.leaveConfidentialSession")}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-xs font-normal leading-normal text-muted-foreground">
            {t("playground.leaveConfidentialSessionDescription")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="grid grid-cols-2 gap-2 px-6 pb-6 pt-2">
          <AlertDialogCancel
            disabled={isLeaving}
            className="mt-0 h-10 rounded-lg border-muted-foreground bg-accent/50 px-6 py-[9.5px]"
          >
            {t("playground.stayHere")}
          </AlertDialogCancel>
          <AlertDialogAction
            className="h-10 rounded-lg bg-destructive px-6 py-[9.5px] text-primary-foreground hover:bg-destructive/90"
            disabled={isLeaving}
            aria-busy={isLeaving}
            onClick={(event) => {
              event.preventDefault();
              if (!isLeaving) void onLeave();
            }}
          >
            {isLeaving ? <Spinner className="size-4 motion-reduce:animate-none" /> : null}
            {isLeaving
              ? t("playground.closingConfidentialSession")
              : t("playground.leaveSession")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
